import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPublicWeddings, searchWeddings } from "../api/weddings";
import {
  DressCode,
  DRESS_CODE_LABELS,
  DiscoverFilters,
  TicketTierRow,
  VibeTag,
  VIBE_TAG_LABELS,
  Wedding,
} from "../types";
import { formatDate, formatPrice } from "../utils/format";
import Loading from "../components/Loading";
import ErrorMessage from "../components/ErrorMessage";

const DRESS_CODES = Object.keys(DRESS_CODE_LABELS) as DressCode[];
const VIBE_TAGS = Object.keys(VIBE_TAG_LABELS) as VibeTag[];
const TEXT_DEBOUNCE_MS = 320;

const coupleNames = (w: Wedding): string => {
  const names = [w.couple_name_1, w.couple_name_2].filter(Boolean);
  if (names.length) return names.join(" & ");
  return w.title;
};

const locationLabel = (w: Wedding): string => {
  const parts = [w.city, w.state].filter(Boolean);
  if (parts.length) return parts.join(", ");
  return w.location || "";
};

const ghostPrice = (tiers?: TicketTierRow[]): number | null => {
  const ghost = tiers?.find((t) => t.tier_type === "ghost_seats" && t.is_active);
  return ghost ? ghost.price_cents : null;
};

const priceRangeLabel = (w: Wedding): string | null => {
  const prices: number[] = [];
  const ghost = ghostPrice(w.ticket_tiers);
  if (ghost != null) prices.push(ghost);
  if (w.ticket_tiers?.length) {
    w.ticket_tiers
      .filter((t) => t.is_active && t.price_cents > 0)
      .forEach((t) => prices.push(t.price_cents));
  }
  if (w.ticket_price != null && w.ticket_price > 0) prices.push(w.ticket_price);
  if (!prices.length) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min) : `${formatPrice(min)} – ${formatPrice(max)}`;
};

const queryFromParams = (params: URLSearchParams) => {
  const q = (params.get("q") || "").trim();
  if (q) return q;
  const search = (params.get("search") || "").trim();
  if (!search || search === "1" || search.toLowerCase() === "true") return "";
  return search;
};

const WeddingCard: React.FC<{ wedding: Wedding }> = ({ wedding: w }) => {
  const price = priceRangeLabel(w);
  const loc = locationLabel(w);
  const names = coupleNames(w);

  return (
    <Link to={`/weddings/${w.id}`} className="wedding-card">
      {w.couple_photo_url && (
        <div className="wedding-card-photo">
          <img src={w.couple_photo_url} alt={names} loading="lazy" />
        </div>
      )}
      <div className="wedding-card-body">
        <h3>{names}</h3>
        <div className="wedding-card-meta">
          <span className="wedding-date">{formatDate(w.date)}</span>
          {loc && <span className="wedding-card-location">{loc}</span>}
        </div>
        {price && <p className="wedding-price">{price}</p>}
        <div className="wedding-card-tags">
          {w.dress_code && (
            <span className="badge">{DRESS_CODE_LABELS[w.dress_code]}</span>
          )}
          {w.vibe_tags?.map((tag) => (
            <span key={tag} className="badge">
              {VIBE_TAG_LABELS[tag]}
            </span>
          ))}
          {w.requires_21_plus && <span className="badge">21+</span>}
          {w.listing_status === "sold_out" && <span className="badge">Sold out</span>}
        </div>
      </div>
    </Link>
  );
};

const SearchResultCard: React.FC<{ wedding: Wedding }> = ({ wedding: w }) => {
  const names = coupleNames(w);
  const loc = locationLabel(w);

  return (
    <Link to={`/weddings/${w.id}`} className="search-result-card">
      <h3>{names}</h3>
      <div className="wedding-card-meta">
        <span className="wedding-date">{formatDate(w.date)}</span>
        {loc && <span className="wedding-card-location">{loc}</span>}
      </div>
    </Link>
  );
};

const buildFiltersFrom = (values: {
  dressCode: string;
  vibe: string;
  requires21: boolean;
  priceMinUsd: string;
  priceMaxUsd: string;
  city: string;
  state: string;
  lastMinuteOnly: boolean;
}): DiscoverFilters => {
  const filters: DiscoverFilters = {};
  if (values.dressCode) filters.dress_code = values.dressCode;
  if (values.vibe) filters.vibe = values.vibe;
  if (values.requires21) filters.requires_21_plus = true;
  if (values.priceMinUsd !== "") {
    const n = Number(values.priceMinUsd);
    if (!Number.isNaN(n)) filters.price_min = Math.round(n * 100);
  }
  if (values.priceMaxUsd !== "") {
    const n = Number(values.priceMaxUsd);
    if (!Number.isNaN(n)) filters.price_max = Math.round(n * 100);
  }
  if (values.city.trim()) filters.city = values.city.trim();
  if (values.state.trim()) filters.state = values.state.trim();
  if (values.lastMinuteOnly) filters.last_minute = true;
  return filters;
};

const PublicWeddings: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = queryFromParams(searchParams);
  const shouldAutoFocus =
    searchParams.has("q") || searchParams.has("search");

  const [weddings, setWeddings] = useState<Wedding[]>([]);
  const [lastMinute, setLastMinute] = useState<Wedding[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState(initialQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState<Wedding[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [dressCode, setDressCode] = useState("");
  const [vibe, setVibe] = useState("");
  const [requires21, setRequires21] = useState(false);
  const [priceMinUsd, setPriceMinUsd] = useState("");
  const [priceMaxUsd, setPriceMaxUsd] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [lastMinuteOnly, setLastMinuteOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Debounced text fields so typing doesn't hammer the API
  const [debouncedCity, setDebouncedCity] = useState("");
  const [debouncedState, setDebouncedState] = useState("");
  const [debouncedPriceMin, setDebouncedPriceMin] = useState("");
  const [debouncedPriceMax, setDebouncedPriceMax] = useState("");

  const requestIdRef = useRef(0);
  const searchRequestIdRef = useRef(0);
  const hasLoadedOnce = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!shouldAutoFocus) return;
    searchInputRef.current?.focus();
  }, [shouldAutoFocus]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), TEXT_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedCity(city), TEXT_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [city]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedState(state), TEXT_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [state]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedPriceMin(priceMinUsd), TEXT_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [priceMinUsd]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedPriceMax(priceMaxUsd), TEXT_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [priceMaxUsd]);

  const activeFilters = useMemo(
    () =>
      buildFiltersFrom({
        dressCode,
        vibe,
        requires21,
        priceMinUsd: debouncedPriceMin,
        priceMaxUsd: debouncedPriceMax,
        city: debouncedCity,
        state: debouncedState,
        lastMinuteOnly,
      }),
    [
      dressCode,
      vibe,
      requires21,
      debouncedPriceMin,
      debouncedPriceMax,
      debouncedCity,
      debouncedState,
      lastMinuteOnly,
    ]
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (dressCode) n += 1;
    if (vibe) n += 1;
    if (requires21) n += 1;
    if (priceMinUsd !== "") n += 1;
    if (priceMaxUsd !== "") n += 1;
    if (city.trim()) n += 1;
    if (state.trim()) n += 1;
    if (lastMinuteOnly) n += 1;
    return n;
  }, [
    dressCode,
    vibe,
    requires21,
    priceMinUsd,
    priceMaxUsd,
    city,
    state,
    lastMinuteOnly,
  ]);

  const load = useCallback(async (filters: DiscoverFilters) => {
    const requestId = ++requestIdRef.current;
    if (hasLoadedOnce.current) {
      setRefreshing(true);
    } else {
      setInitialLoading(true);
    }
    setError("");

    try {
      const [{ data: main }, { data: lm }] = await Promise.all([
        getPublicWeddings(filters),
        getPublicWeddings({ ...filters, last_minute: true }),
      ]);

      // Ignore outdated responses if a newer filter request started
      if (requestId !== requestIdRef.current) return;

      startTransition(() => {
        setWeddings(main);
        setLastMinute(lm);
      });
      hasLoadedOnce.current = true;
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load weddings");
    } finally {
      if (requestId === requestIdRef.current) {
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    load(activeFilters);
  }, [activeFilters, load]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!q) {
      setSearchResults([]);
      setSearchError("");
      setSearching(false);
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setSearching(true);
    setSearchError("");

    searchWeddings(q)
      .then(({ data }) => {
        if (requestId !== searchRequestIdRef.current) return;
        setSearchResults(data);
      })
      .catch((err) => {
        if (requestId !== searchRequestIdRef.current) return;
        setSearchResults([]);
        setSearchError(err instanceof Error ? err.message : "Search failed");
      })
      .finally(() => {
        if (requestId === searchRequestIdRef.current) setSearching(false);
      });
  }, [debouncedSearch]);

  const handleClear = () => {
    setDressCode("");
    setVibe("");
    setRequires21(false);
    setPriceMinUsd("");
    setPriceMaxUsd("");
    setCity("");
    setState("");
    setLastMinuteOnly(false);
    setDebouncedCity("");
    setDebouncedState("");
    setDebouncedPriceMin("");
    setDebouncedPriceMax("");
  };

  if (initialLoading && !hasLoadedOnce.current) {
    return <Loading message="Loading weddings..." />;
  }

  const hasSearchQuery = Boolean(debouncedSearch.trim());

  return (
    <div className="page">
      <h1>Discover</h1>
      <p className="form-hint" style={{ textAlign: "center", marginTop: "-0.5rem" }}>
        Browse available public weddings. Find a night worth showing up for.
      </p>
      <div className="golden-thread" aria-hidden="true" />

      <div className="discover-search">
        <label htmlFor="discover-search">Search by couple name</label>
        <input
          ref={searchInputRef}
          id="discover-search"
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          autoComplete="off"
          enterKeyHint="search"
        />
      </div>

      <div className={`filters filters-collapsible${filtersOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="filters-toggle"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <span>
            Filters
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </span>
          <span className="filters-toggle-icon" aria-hidden="true">
            {filtersOpen ? "−" : "+"}
          </span>
        </button>
        {filtersOpen && (
          <div className="filters-body">
            <p className="form-hint filters-live-hint">
              Results update as you change filters.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="filter-dress">Dress Code</label>
                <select
                  id="filter-dress"
                  value={dressCode}
                  onChange={(e) => setDressCode(e.target.value)}
                >
                  <option value="">Any</option>
                  {DRESS_CODES.map((d) => (
                    <option key={d} value={d}>
                      {DRESS_CODE_LABELS[d]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="filter-vibe">Vibe</label>
                <select
                  id="filter-vibe"
                  value={vibe}
                  onChange={(e) => setVibe(e.target.value)}
                >
                  <option value="">Any</option>
                  {VIBE_TAGS.map((v) => (
                    <option key={v} value={v}>
                      {VIBE_TAG_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="filter-price-min">Min Price (USD)</label>
                <input
                  id="filter-price-min"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={priceMinUsd}
                  onChange={(e) => setPriceMinUsd(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="filter-price-max">Max Price (USD)</label>
                <input
                  id="filter-price-max"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Any"
                  value={priceMaxUsd}
                  onChange={(e) => setPriceMaxUsd(e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="filter-city">City</label>
                <input
                  id="filter-city"
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="filter-state">State</label>
                <input
                  id="filter-state"
                  type="text"
                  placeholder="State"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  maxLength={24}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={requires21}
                    onChange={(e) => setRequires21(e.target.checked)}
                  />
                  21+ only
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={lastMinuteOnly}
                    onChange={(e) => setLastMinuteOnly(e.target.checked)}
                  />
                  Last-minute only
                </label>
              </div>
            </div>

            <div className="button-row">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClear}
                disabled={activeFilterCount === 0}
              >
                Clear filters
              </button>
              {refreshing && (
                <span className="filters-refreshing" aria-live="polite">
                  Updating…
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <ErrorMessage message={error} onRetry={() => load(activeFilters)} />}

      {hasSearchQuery && (
        <section className="discover-search-results" aria-live="polite">
          <h2 className="profile-section-title">Search results</h2>
          {searching && <p className="form-hint">Searching every live wedding…</p>}
          {searchError && <p className="field-error">{searchError}</p>}
          {!searching && !searchError && searchResults.length === 0 && (
            <p className="empty-state">
              No weddings match that name. Try another spelling — search looks across
              every live wedding on the platform, not only Discover listings.
            </p>
          )}
          {!searching && searchResults.length > 0 && (
            <div className="search-result-list">
              {searchResults.map((w) => (
                <SearchResultCard key={w.id} wedding={w} />
              ))}
            </div>
          )}
        </section>
      )}

      <div
        className={`discover-results${refreshing ? " is-refreshing" : ""}`}
        aria-busy={refreshing}
      >
        <section className="discover-section">
          <h2 className="profile-section-title">Open Listings</h2>
          {weddings.length === 0 ? (
            <p className="empty-state">No weddings match your filters.</p>
          ) : (
            <div className="wedding-grid">
              {weddings.map((w) => (
                <WeddingCard key={w.id} wedding={w} />
              ))}
            </div>
          )}
        </section>

        {!lastMinuteOnly && (
          <section className="discover-section" style={{ marginTop: "2.5rem" }}>
            <h2 className="profile-section-title">Last-Minute</h2>
            {lastMinute.length === 0 ? (
              <p className="empty-state">No last-minute openings right now.</p>
            ) : (
              <div className="wedding-grid">
                {lastMinute.map((w) => (
                  <WeddingCard key={w.id} wedding={w} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default PublicWeddings;
